import { useContext, useEffect, useMemo, useState } from 'react'
import { DesignContext } from 'contexts/DesignContext'

import { ConfigContext } from 'contexts/ConfigContext'
import objectPath from 'object-path'
import { Form, Col, Row, InputGroup } from 'react-bootstrap'
import Label from './Label'
import InputPrepend from './InputPrepend'
import InputAppend from './InputAppend'
import {
  cancelDebounceCallback,
  debounceCallback,
} from 'utility/debounceCallback.util'
import { setApi, getServiceRequest, getExternalRequest } from '../action/Api'
import useAuth from 'hooks/useAuth'
import NumberFormat from 'react-number-format'
import { createUischemaStyle } from './Style'
import { handleEnterAction } from '../action/Action'

const CustomInput = (props) => {
  let {
    uischema,
    updateData,
    copyData = () => {},
    errors = {},
    value,
    label,
    setIsOnChange,
    fieldName,
    fieldKey,
    config = {},
  } = props
  let {
    format,
    name,
    key,
    elementWidth,
    placeholder,
    minLength,
    maxLength,
    readonly,
    controlId,
    suffixText,
    require,
    prependIcon = null,
    appendIcon = null,
    prependIconTooltip = null,
    appendIconTooltip = null,
  } = uischema

  const designContext = useContext(DesignContext)
  const {
    elementAction,
    jsonForm,
    getCurrentUi,
    updateUiSchema,
    isResetData,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state
  const { user } = useAuth()

  let errorArray = objectPath.get(errors, key) || []
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  let actionUpdate = objectPath.get(elementAction, ['actionUpdate'])
  let updateOption = objectPath.get(elementAction, ['updateOption'])

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire }),
    [uischema, showRequire],
  )

  const updateSelectOption = () => {
    if (actionUpdate) {
      actionUpdate.forEach(async (selectKey) => {
        let isUpdateOption = objectPath.get(updateOption, selectKey)
        if (!isUpdateOption) return
        let selectUi = getCurrentUi(selectKey)
        let { optionType } = selectUi
        let optionData = objectPath.get(selectUi, `${optionType}`)
        let { service, endpoint, pathParams, queryParams, url } = optionData

        let api = setApi(
          {
            apiType: optionType,
            service: service,
            endpoint: endpoint,
            pathParams: pathParams,
            queryParams: queryParams,
            jsonForm,
          },
          jsonForm.formData,
        )
        if (optionType === 'internal') {
          if (!service || !endpoint) return
          optionData = await getServiceRequest(api, optionData, selectUi)
        } else if (optionType === 'external') {
          if (!url) return
          optionData = await getExternalRequest(api, optionData, selectUi)
        }
        updateUiSchema(selectUi.key, optionType, optionData)
      })
    }
  }

  useEffect(() => {
    debounceCallback(updateSelectOption, [])
  }, [value])

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    [],
  )
  const inputValue =
    format === 'number'
      ? Number(value)
      : format === 'user'
      ? user.name
      : value ?? ''
  const isDebounceInput = format !== 'number' && format !== 'user'
  const [localValue, setLocalValue] = useState(inputValue)
  const debounceInputName = `input-update-${fieldKey}`
  const isResetField = objectPath.get(isUiChanged, ['isResetField', key])
  const updateInputData = (value) => {
    updateData(value, true)
    copyData(value)
  }

  useEffect(() => {
    setLocalValue(inputValue)
  }, [inputValue])

  useEffect(() => {
    if (!isResetData && !isResetField) return
    setLocalValue(inputValue)
    if (isResetField) {
      objectPath.del(isUiChanged, ['isResetField', key])
      setIsUiChanged(isUiChanged)
    }
  }, [isResetData, isResetField, inputValue])

  useEffect(() => {
    if (format === 'user') {
      let value = objectPath.get(user, 'userId')
      updateData(value, false)
    }
  }, [format])

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          uischema={uischema}
          label={label}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col sm={elementWidth} className="mt-1 p-0">
          <InputGroup>
            <InputPrepend
              {...props}
              isReadOnly={isReadOnly}
              uischema={uischema}
            />
            {format === 'currency' ? (
              <NumberFormat
                className="form-control"
                style={uiStyle}
                name="value"
                thousandSeparator={format === 'currency'}
                autoComplete="off"
                value={value}
                readOnly={isReadOnly}
                onChange={(e) => {
                  let stringValue = e.target.value
                  let replaceString = stringValue.replace(',', '')
                  let numberValue = Number(replaceString)
                  updateData(numberValue, true)
                  copyData(numberValue)
                  setIsOnChange(true)
                }}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    handleEnterAction(ev, uischema, designContext, config)
                    ev.preventDefault()
                    ev.stopPropagation()
                  }
                }}
              />
            ) : (
              <Form.Control
                style={uiStyle}
                type={format}
                id={fieldKey}
                name={fieldName}
                minLength={minLength}
                maxLength={maxLength}
                placeholder={placeholder}
                autoComplete="off"
                readOnly={isReadOnly}
                value={isDebounceInput ? localValue : inputValue}
                onChange={(e) => {
                  let value = e.target.value
                  if (format === 'number') {
                    value = Number(value)
                  }
                  if (isDebounceInput) {
                    setLocalValue(value)
                    debounceCallback(
                      updateInputData,
                      [value],
                      debounceInputName,
                      250,
                    )
                  } else {
                    updateInputData(value)
                  }
                  setIsOnChange(true)
                }}
                onBlur={(e) => {
                  if (!isDebounceInput) return
                  cancelDebounceCallback(debounceInputName)
                  updateInputData(e.target.value)
                }}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') {
                    if (isDebounceInput) {
                      cancelDebounceCallback(debounceInputName)
                      updateInputData(ev.target.value)
                    }
                    handleEnterAction(ev, uischema, designContext, config)
                    ev.preventDefault()
                    ev.stopPropagation()
                  }
                }}
              />
            )}

            <InputAppend
              {...props}
              isReadOnly={isReadOnly}
              uischema={uischema}
            />
          </InputGroup>
        </Col>
        {suffixText && (
          <Col className="d-flex align-items-center mt-1 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`} style={{ color: colorByTheme }}>
              {suffixText}
            </Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${key}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomInput
